<template>
  <Popup
    :visible="visible"
    :teleport="teleport"
    position="center"
    :close-on-click-overlay="false"
    :class="classes(n('wrapper'))"
    @update:visible="onUpdateVisible"
  >
    <div :class="classes(n(), n(`--${theme}`))">
      <!-- 内容区域 -->
      <div :class="n('content')">
        <!-- 标题插槽 -->
        <div
          v-if="$slots.title || title"
          :class="n('title')"
        >
          <slot name="title">
            {{ title }}
          </slot>
        </div>

        <!-- 内容插槽 -->
        <div
          v-if="$slots.default || content"
          :class="[
            n('message'),
            { [n('message--no-title')]: !$slots.title && !title },
          ]"
        >
          <slot>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="content" />
          </slot>
        </div>
      </div>

      <!-- 操作按钮区域 -->
      <div
        v-if="$slots.actions || actions.length > 0"
        :class="[
          n('actions'),
          n(`actions--${computedButtonLayout}`),
        ]"
      >
        <slot name="actions">
          <div
            v-for="(action, index) in actions"
            :key="index"
            :class="[
              n('action'),
              n(`action--${getActionType(index)}`),
            ]"
            @click="handleActionClick(action, index)"
          >
            {{ action.text }}
          </div>
        </slot>
      </div>
    </div>
  </Popup>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'virtual-vue';
import { props } from './props';
import { emits } from './emits';
import { createNamespace } from '../utils/components';
import Popup from '../popup/popup.vue';
import type { DialogAction } from './dialog';

const { name, n, classes } = createNamespace('dialog');


export default defineComponent({
  name,
  components: { Popup },
  props,
  emits,
  setup(props, { emit }) {
    /**
     * 计算按钮布局
     * 1个或3+个按钮时恒定为垂直布局
     * 2个按钮时默认水平布局，但可通过 buttonLayout prop 切换为垂直布局
     */
    const computedButtonLayout = computed(() => {
      const buttonCount = props.actions.length;

      // 1个或3+个按钮：恒定垂直布局，不受 buttonLayout prop 影响
      if (buttonCount === 1 || buttonCount >= 3) {
        return 'vertical';
      }

      // 2个按钮：默认水平布局，可通过 buttonLayout 切换为垂直布局
      if (props.buttonLayout) {
        return props.buttonLayout;
      }
      return 'horizontal';
    });

    /**
     * 获取按钮类型
     * @param index 按钮索引
     *
     * 按钮颜色规则：
     * - 1个按钮：主操作（蓝色）
     * - 2个按钮水平布局：第一个辅助操作（黑色），第二个主操作（蓝色）
     * - 2个按钮垂直布局：第一个主操作（蓝色），第二个辅助操作（黑色）
     * - 3+个按钮：前面都是主操作（蓝色），最后一个辅助操作（黑色）
     * - 警示主题：最后一个按钮为危险操作（红色）
     */
    const getActionType = (index: number): string => {
      const isLastButton = index === props.actions.length - 1;
      const buttonCount = props.actions.length;

      // 警示主题：最后一个按钮为危险操作
      if (props.theme === 'warn' && isLastButton) {
        return 'danger';
      }

      // 1个按钮的情况：主操作
      if (buttonCount === 1) {
        return 'primary';
      }

      // 2个按钮的情况：根据布局决定颜色
      if (buttonCount === 2) {
        const layout = computedButtonLayout.value;
        // 水平布局：第一个是辅助操作，第二个是主操作
        if (layout === 'horizontal') {
          return isLastButton ? 'primary' : 'secondary';
        }
        // 垂直布局：第一个是主操作，第二个是辅助操作
        return isLastButton ? 'secondary' : 'primary';
      }

      // 3+个按钮：最后一个是辅助操作，其他是主操作
      return isLastButton ? 'secondary' : 'primary';
    };

    /**
     * 处理按钮点击
     * @param action 按钮配置
     * @param index 按钮索引
     */
    const handleActionClick = (action: DialogAction, index: number) => {
      // 执行按钮自定义点击事件
      if (action.click) {
        action.click();
      }

      // 触发组件 click 事件
      emit('click', index);

      // 自动关闭对话框
      emit('update:visible', false);
    };

    /**
     * 处理 visible 更新
     * @param visible 新的 visible 状态
     */
    const onUpdateVisible = (visible: boolean) => {
      emit('update:visible', visible);
    };

    return {
      n,
      classes,
      computedButtonLayout,
      getActionType,
      handleActionClick,
      onUpdateVisible,
    };
  },
});
</script>

